import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { config } from '../../config/config';

const authHeaders = () => {
  const token = localStorage.getItem('token');
  return token ? { Authorization: `Bearer ${token}` } : {};
};

export const fetchNotifications = createAsyncThunk(
  'notifications/fetchNotifications',
  async (params = {}, { rejectWithValue }) => {
    try {
      const qs = new URLSearchParams();
      if (params.page) qs.set('page', params.page);
      if (params.limit) qs.set('limit', params.limit);
      if (params.unread) qs.set('unread', 'true');
      const url = `${config.apiUrl}/api/notifications${qs.toString() ? `?${qs}` : ''}`;
      const res = await fetch(url, { headers: { ...authHeaders() } });
      const data = await res.json();
      if (!res.ok || !data.success) return rejectWithValue(data.message || 'Failed to fetch notifications');
      return data;
    } catch (err) {
      return rejectWithValue(err.message || 'Network error');
    }
  }
);

export const fetchUnreadCount = createAsyncThunk(
  'notifications/fetchUnreadCount',
  async (_, { rejectWithValue }) => {
    try {
      const res = await fetch(`${config.apiUrl}/api/notifications/unread-count`, {
        headers: { ...authHeaders() }
      });
      const data = await res.json();
      if (!res.ok || !data.success) return rejectWithValue(data.message || 'Failed');
      return data.unreadCount || 0;
    } catch (err) {
      return rejectWithValue(err.message || 'Network error');
    }
  }
);

export const markNotificationRead = createAsyncThunk(
  'notifications/markRead',
  async (id, { rejectWithValue }) => {
    try {
      const res = await fetch(`${config.apiUrl}/api/notifications/${id}/read`, {
        method: 'PATCH',
        headers: { ...authHeaders() }
      });
      const data = await res.json();
      if (!res.ok || !data.success) return rejectWithValue(data.message || 'Failed');
      return data.notification;
    } catch (err) {
      return rejectWithValue(err.message || 'Network error');
    }
  }
);

export const markAllNotificationsRead = createAsyncThunk(
  'notifications/markAllRead',
  async (_, { rejectWithValue }) => {
    try {
      const res = await fetch(`${config.apiUrl}/api/notifications/read-all`, {
        method: 'PATCH',
        headers: { ...authHeaders() }
      });
      const data = await res.json();
      if (!res.ok || !data.success) return rejectWithValue(data.message || 'Failed');
      return data;
    } catch (err) {
      return rejectWithValue(err.message || 'Network error');
    }
  }
);

export const deleteNotification = createAsyncThunk(
  'notifications/delete',
  async (id, { rejectWithValue }) => {
    try {
      const res = await fetch(`${config.apiUrl}/api/notifications/${id}`, {
        method: 'DELETE',
        headers: { ...authHeaders() }
      });
      const data = await res.json();
      if (!res.ok || !data.success) return rejectWithValue(data.message || 'Failed');
      return id;
    } catch (err) {
      return rejectWithValue(err.message || 'Network error');
    }
  }
);

export const clearReadNotifications = createAsyncThunk(
  'notifications/clearRead',
  async (_, { rejectWithValue }) => {
    try {
      const res = await fetch(`${config.apiUrl}/api/notifications?readOnly=true`, {
        method: 'DELETE',
        headers: { ...authHeaders() }
      });
      const data = await res.json();
      if (!res.ok || !data.success) return rejectWithValue(data.message || 'Failed');
      return true;
    } catch (err) {
      return rejectWithValue(err.message || 'Network error');
    }
  }
);

const initialState = {
  items: [],
  unreadCount: 0,
  loading: false,
  error: null,
  pagination: { page: 1, limit: 30, total: 0, pages: 0 }
};

const slice = createSlice({
  name: 'notifications',
  initialState,
  reducers: {
    resetNotifications: () => initialState
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchNotifications.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchNotifications.fulfilled, (state, action) => {
        state.loading = false;
        state.items = action.payload.notifications || [];
        state.unreadCount = action.payload.unreadCount ?? state.unreadCount;
        state.pagination = action.payload.pagination || state.pagination;
      })
      .addCase(fetchNotifications.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(fetchUnreadCount.fulfilled, (state, action) => {
        state.unreadCount = action.payload || 0;
      })
      .addCase(markNotificationRead.fulfilled, (state, action) => {
        const n = action.payload;
        if (!n) return;
        const idx = state.items.findIndex((i) => i._id === n._id);
        if (idx !== -1) state.items[idx] = { ...state.items[idx], ...n };
        state.unreadCount = Math.max(0, state.unreadCount - 1);
      })
      .addCase(markAllNotificationsRead.fulfilled, (state) => {
        state.items = state.items.map((i) => ({ ...i, read: true }));
        state.unreadCount = 0;
      })
      .addCase(deleteNotification.fulfilled, (state, action) => {
        const id = action.payload;
        const removed = state.items.find((i) => i._id === id);
        state.items = state.items.filter((i) => i._id !== id);
        if (removed && !removed.read) state.unreadCount = Math.max(0, state.unreadCount - 1);
      })
      .addCase(clearReadNotifications.fulfilled, (state) => {
        state.items = state.items.filter((i) => !i.read);
      });
  }
});

export const { resetNotifications } = slice.actions;
export default slice.reducer;
